import { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { ClipboardList, Eye } from "lucide-react";
import dayjs from "dayjs";

import { ContainerCard, ModuleTitleCard } from "../../components/ui/cards";
import { Button } from "../../components/ui/button";
import { Reactable } from "../../components/ui/reactable";
import ToastMsg from "../../components/common/ToastMsg";
import useApiMethods from "../../lib/useApiMethods";
import { APPRAISALS } from "../../constants/api";

import StatusPill, { STATUS_META } from "../../components/appraisal/StatusPill";

// Status options mirror the backend lifecycle keys.
const STATUS_OPTIONS = [
    "pending_goal_agreement",
    "goals_sent_back",
    "goals_agreed",
    "pending_employee_submission",
    "in_progress",
    "submitted_to_rm",
    "sent_back_by_rm",
    "pending_hr_approval",
    "hr_approved",
    "published",
    "overdue",
    "closed",
].map((s) => ({ value: s, label: STATUS_META[s]?.label || s.replace(/_/g, " ") }));

/* Columns carry their own filters, so the screen needs no loose controls:
   Reactable puts every `filterable` column in its funnel popover and supplies
   the search box and export buttons itself. Client-side matching reads the raw
   value at `accessor`, so anything nested is flattened onto the row first. */
const buildColumns = (onOpen) => [
    {
        accessor: "cycle_name", Header: "Cycle", sortable: true, filterable: true,
        Cell: ({ row }) => (
            <button
                className="font-medium hover:text-sidebar-primary text-left"
                onClick={(e) => { e.stopPropagation(); onOpen(row.id); }}
            >
                {row.cycle_name || "—"}
            </button>
        ),
    },
    {
        accessor: "reporting_manager_name", Header: "Reporting Manager",
        sortable: true, filterable: true,
        Cell: ({ value }) => value || "—",
    },
    {
        accessor: "self_submitted_at", Header: "Submitted", sortable: true,
        filterable: true, filterType: "date",
        Cell: ({ value }) => (value ? dayjs(value).format("DD-MM-YYYY") : "—"),
    },
    {
        accessor: "status", Header: "Status", sortable: true, filterable: true,
        filterType: "select", filterOptions: STATUS_OPTIONS,
        Cell: ({ value }) => <StatusPill value={value} />,
    },
    {
        accessor: "action", Header: "Action",
        Cell: ({ row }) => (
            <Button variant="ghost" size="sm"
                onClick={(e) => { e.stopPropagation(); onOpen(row.id); }}>
                <Eye className="w-4 h-4 mr-1" /> Open
            </Button>
        ),
    },
];

const MyAppraisalList = () => {
    const navigate = useNavigate();
    const { fetch } = useApiMethods();

    const [appraisals, setAppraisals] = useState([]);
    const [loading, setLoading] = useState(false);

    const loadData = useCallback(async () => {
        setLoading(true);
        try {
            const res = await fetch(APPRAISALS, { scope: "mine" });
            setAppraisals(res?.data || []);
        } catch (_) {
            ToastMsg("error", "Failed to load appraisals");
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => { loadData(); }, [loadData]);

    // Flattened so the manager column can be sorted and filtered like any other.
    const rows = useMemo(
        () => appraisals.map((a) => ({
            ...a,
            reporting_manager_name: a.reporting_manager?.name || "",
        })),
        [appraisals],
    );

    return (
        <ContainerCard scrollable>
            <ModuleTitleCard
                Icon={ClipboardList}
                title="My Appraisal"
                subtitle="Appraisal cycles assigned to you"
                totalRecords={rows.length}
            />

            {loading ? (
                <div className="text-center py-10 text-muted-foreground">Loading…</div>
            ) : (
                <Reactable
                    pagename="my-appraisals"
                    data={rows}
                    setData={setAppraisals}
                    columns={buildColumns((id) => navigate(`/appraisal/my-appraisal/${id}`))}
                    isCSVRequired
                    isPDFRequired
                    url={APPRAISALS}
                    printFilter={{ scope: "mine" }}
                    rowSize="sm"
                    tableHeight="calc(100vh - 300px)"
                />
            )}
        </ContainerCard>
    );
};

export default MyAppraisalList;
